import React, { ComponentProps, FC, ReactNode } from 'react';

import { useNavigate } from 'react-router-dom';

import useComposeType from '../../../hooks/router/search/useComposeType';
import useIsComposeOpen from '../../../hooks/router/search/useIsComposeOpen';
import translate from '../../../i18n/translate';

import { DialogId } from '../../../models/dialog';
import ButtonBack from '../../MUI/ButtonBack';

import ConfirmPopover from '../../MUI/ConfirmPopover';
import HeaderSegmentedButtons from '../SegmentedButtons';

import HeaderMobileTitle from './HeaderMobileTitle';
import { goBack } from './helpers';

import * as Styled from './styled';

export type CustomBackAction = {
  dialogId: DialogId;
  onConfirm: () => void;
  shouldConfirm: boolean;
};

export type Props = {
  action?: () => void;
  backUrl?: string | true;
  children?: ReactNode;
  contentRight?: ReactNode;
  customBackAction?: CustomBackAction;
  isLoading?: boolean;
  segmentedButtons?: ComponentProps<typeof HeaderSegmentedButtons>['buttons'];
  title: ReactNode;
};

/**
 * Mobile header component for feed and details view
 *
 * @param props                  props passed to the component
 * @param props.action           Action that will be executed on back button click
 * @param props.backUrl          Route back button should redirect to / If true, use native back
 * @param props.children         Children that will be shown in the subheader (segmented buttons, quick filters...)
 * @param props.contentRight     Content that should be shown on the right side of the header
 * @param props.customBackAction Custom action for clicking back - trigger modal if default draft was changed
 * @param props.isLoading        Whether data required for the header is loading
 * @param props.segmentedButtons Segmented buttons that will be shown within the subheader to navigate between different views
 * @param props.title            title that will be shown in the header
 *
 * @returns                      The component itself
 */
const BaseHeaderMobile: FC<Props> = ({
  action,
  backUrl = null,
  children = null,
  contentRight = null,
  customBackAction = null,
  isLoading = false,
  segmentedButtons = [],
  title,
}) => {
  const navigate = useNavigate();
  const composeType = useComposeType();
  const isComposeOpen = useIsComposeOpen();

  const dataComposeType = isComposeOpen ? composeType : null;

  return (
    <>
      <Styled.HeaderContent data-compose-type={dataComposeType}>
        <Styled.LeftElementContainer>
          {backUrl !== null &&
            (customBackAction?.shouldConfirm ? (
              <ConfirmPopover
                onConfirm={customBackAction.onConfirm}
                shouldConfirm={true}
                trigger={<ButtonBack label={translate('GENERAL__BACK')} />}
                type={customBackAction.dialogId}
              />
            ) : (
              <ButtonBack
                label={translate('GENERAL__BACK')}
                onClick={() => {
                  action?.();
                  goBack(backUrl, navigate);
                }}
              />
            ))}
        </Styled.LeftElementContainer>

        <HeaderMobileTitle isLoading={isLoading} title={title} />

        <Styled.RightElementContainer>
          {contentRight}
        </Styled.RightElementContainer>
      </Styled.HeaderContent>
      <Styled.SubheaderWrapper>
        <Styled.SegmentedButtons buttons={segmentedButtons} />
        {children}
      </Styled.SubheaderWrapper>
    </>
  );
};

export default BaseHeaderMobile;
